// RebalanceCard — Process 3. Drift-from-target axis + net drift, rebalance
// methods, and account activity. Built on ProcessCard. Uses the reusable
// StatusPill for the "Within target" status.
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cx } from '../../../utils/cx';
import { ProcessCard, StatusPill } from '../../reusable';
import { CheckIcon } from '../../icons/Icons';
import { ScaleIcon, AutoUpIcon } from './manage-icons';
import { DriftAxis } from './DriftAxis';

export function RebalanceCard({ rebalance, allocations, onAction, open, onToggle }) {
  const { t } = useTranslation();
  return (
    <ProcessCard
      open={open}
      onToggle={onToggle}
      className="ma-rebalance"
      tone="green"
      icon={<ScaleIcon />}
      title={t('manage.rebalance.title')}
      question={t('manage.rebalance.question')}
      ctaTone="blue"
      ctaLabel={t('manage.rebalance.cta')}
      onCta={onAction}
      summary={t('manage.rebalance.summary', {
        drift: rebalance.netDrift.toFixed(1),
        state: rebalance.autoOn ? t('common.on') : t('common.off'),
        next: rebalance.next,
      })}
    >
      <div className="ma-rebal-grid">
        <div className="ma-panel">
          <div className="ma-panel__h ma-panel__h--split">
            <span>{t('manage.rebalance.driftFromTarget')}</span>
            <StatusPill tone="success">{rebalance.status}</StatusPill>
          </div>
          <DriftAxis allocations={allocations} />
          <div className="ma-net-drift">
            <span className="ma-net-drift__lbl">{t('manage.rebalance.netDrift')}</span>
            <span className="ma-net-drift__val">{rebalance.netDrift.toFixed(1)}%</span>
            <span className="ma-net-drift__sub">{t('manage.rebalance.withinBand')}</span>
          </div>
        </div>

        <div className="ma-side-panel">
          <div className="ma-side__h">{t('manage.rebalance.methods')}</div>
          <ul className="ma-rebal-types">
            {rebalance.methods.map((m) => (
              <li key={m.key}>
                <div className="ma-rebal-type__name">{m.name}</div>
                <div className="ma-rebal-type__desc">{m.desc}</div>
                <span className={cx('ma-rebal-type__status', m.status.startsWith('On') && 'on')}>{m.status}</span>
              </li>
            ))}
          </ul>

          <div className="ma-side__h ma-side__h--gap">{t('manage.rebalance.accountActivity')}</div>
          <dl className="ma-rules">
            <div>
              <dt>{t('manage.rebalance.autoRebalance')}</dt>
              <dd>
                <span className="ma-on-pill">
                  <AutoUpIcon /> {t('common.on')}
                </span>{' '}
                {rebalance.cadence}
              </dd>
            </div>
            <div>
              <dt>{t('manage.rebalance.nextScheduled')}</dt>
              <dd>{rebalance.next}</dd>
            </div>
            <div>
              <dt>{t('manage.rebalance.lastRebalance')}</dt>
              <dd>
                {rebalance.lastRebalance} <span className="ma-muted">{t('manage.rebalance.fundsParen', { count: rebalance.lastRebalanceFunds })}</span>
              </dd>
            </div>
            <div>
              <dt>{t('manage.rebalance.pendingBlockers')}</dt>
              <dd className="ma-pos">
                <CheckIcon width={13} height={13} /> {t('manage.rebalance.noBlockers')}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </ProcessCard>
  );
}

RebalanceCard.propTypes = {
  rebalance: PropTypes.object.isRequired,
  allocations: PropTypes.array.isRequired,
  onAction: PropTypes.func,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default RebalanceCard;
